import React, {Component, ReactNode} from 'react'
import ResidentGeneralMeetingRepo, {ResidentGeneralMeetingInfo} from './ResidentGeneralMeetingRepo'
import {StyledCard, StyledPageTitle} from '../reservations/components/CommonStyle'
import {StyledSecondaryButton} from '../../styledComponents/Button'
import styled from 'styled-components'
import {currentMedia} from '../../styledComponents/ResponsiveConvenience'
import {
    ColumnFlexContainer,
    FullWidthLeftColumnFlexContainer,
    FullWidthLeftRowFlexContainer,
    FullWidthRightColumnFlexContainer,
    FullWidthRowFlexContainer
} from '../../styledComponents/FlexComponents'
import {formatDateWithDayOfTheWeekFromDate} from '../../formatters/formatDate'
import {Box} from '@mui/material'
import {MiddleFontSpan} from '../assocDirector/AssocDirectorCommon'
import {Colors, FontSize, FontWeights} from '../../styledComponents/Constants'
import {CalendarMonth} from '@mui/icons-material'
import {VOTING_PREFERENCE_STATUS_SUMMARY} from '../../CodeMaster'
import {HttpError} from '../../network/errors/HttpError'
import {Messages} from '../../Messages'
import {PlainForwardLink} from '../../styledComponents/Links'
import {
    HeadingAreaMessageComponentForResident
} from '../shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../shared/hoc/createResidentWithErrorHandlingPage'

interface Props {
    condominiumCd: string
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
}

interface State {
    residentGeneralMeetingInfoList: ResidentGeneralMeetingInfo[]
    showErrorComponent: boolean
    isLoadCompleted: boolean
}

class ResidentGeneralMeetingListPage extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            residentGeneralMeetingInfoList: [],
            showErrorComponent: false,
            isLoadCompleted: false,
        }

        this.renderGeneralMeetingCards = this.renderGeneralMeetingCards.bind(this)
        this.handlePastGeneralMeetingButtonClick = this.handlePastGeneralMeetingButtonClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {
            condominiumCd,
            residentGeneralMeetingRepo,
            eventEmitter,
        } = this.props

        await eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                residentGeneralMeetingRepo.getResidentGeneralMeetingInfoList.bind(
                    residentGeneralMeetingRepo,
                    condominiumCd,
                    false,
                    false
                ),
                async (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError instanceof HttpError) {
                        if (httpErrorOrNetworkError.responseBody.requestError.messageId === Messages.EOPCC0063.id) {
                            this.setState({
                                showErrorComponent: true,
                                isLoadCompleted: true,
                            }, async () => {
                                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                            })
                        }
                    }
                },
            )

            if (response === null) {
                return
            }

            this.setState({
                residentGeneralMeetingInfoList: response.details,
                isLoadCompleted: true,
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleGeneralMeetingCardClick(generalMeetingId: number): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/general-meetings/${generalMeetingId}`,
            {
                state: {
                    previousPageUrl: `/resident/condominiums/${this.props.condominiumCd}/general-meetings`,
                },
            },
        )
    }

    handlePastGeneralMeetingButtonClick(): void {
        this.props.navigate(`/resident/condominiums/${this.props.condominiumCd}/general-meetings/past`)
    }

    renderGeneralMeetingCards(): ReactNode {
        const {
            residentGeneralMeetingInfoList,
        } = this.state

        if (residentGeneralMeetingInfoList.length === 0) {
            return <Box marginTop={'15px'} marginBottom={'10px'}>
                <MiddleFontSpan>近日開催予定の総会はありません</MiddleFontSpan>
            </Box>
        } else {
            return <ContentBody>
                <GeneralMeetingCardList>
                    {residentGeneralMeetingInfoList.map((generalMeetingInfo) => {
                        return <NoMarginStyledCard
                            key={generalMeetingInfo.generalMeetingId}
                            onClick={() => this.handleGeneralMeetingCardClick(generalMeetingInfo.generalMeetingId)}
                        >
                            <FullHeightColumnFlexContainer>
                                <CardHeaderArea status={generalMeetingInfo.votingPreferenceStatusSummary}>
                                    <DeadlineArea>
                                        期限：
                                        {formatDateWithDayOfTheWeekFromDate(
                                            generalMeetingInfo.votingPreferenceDeadline
                                        )}
                                    </DeadlineArea>
                                    <VotingPreferenceStatusLabel
                                        status={generalMeetingInfo.votingPreferenceStatusSummary}
                                    >
                                        {generalMeetingInfo.votingPreferenceStatusSummary.label}
                                    </VotingPreferenceStatusLabel>
                                </CardHeaderArea>
                                <CardMainArea>
                                    <NameArea>
                                        {generalMeetingInfo.generalMeetingName}
                                    </NameArea>
                                    <DateArea>
                                        <StyledCalendarMonth/>
                                        <Box width={'5px'}/>
                                        {formatDateWithDayOfTheWeekFromDate(generalMeetingInfo.generalMeetingDate)}
                                    </DateArea>
                                </CardMainArea>
                            </FullHeightColumnFlexContainer>
                        </NoMarginStyledCard>
                    })}
                </GeneralMeetingCardList>
                <Box height={'5px'}/>
            </ContentBody>
        }
    }

    render(): ReactNode {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return <>
            <Box height={'15px'}/>
            <StyledPageTitle>総会一覧</StyledPageTitle>
            <Box height={'20px'}/>
            <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
            {this.state.showErrorComponent
                ? <FullWidthRightColumnFlexContainer>
                    <PlainForwardLink to={'/resident/contactus'} target={'_blank'}>
                        お問い合わせはこちら
                    </PlainForwardLink>
                    <ContactType>素敵ネットの登録・操作について</ContactType>
                </FullWidthRightColumnFlexContainer>
                : <>
                    {this.renderGeneralMeetingCards()}
                    <PastGeneralMeetingButton onClick={this.handlePastGeneralMeetingButtonClick}>
                        過去の総会を見る
                    </PastGeneralMeetingButton>
                </>
            }
            <Box height={'15px'}/>
        </>
    }
}

export default createResidentWithErrorHandlingPage(ResidentGeneralMeetingListPage)

const ContentBody = styled.div`
  max-width: 738px;
  margin: auto;
`

const GeneralMeetingCardList = styled.div`
  display: flex;
  box-sizing: border-box;
  ${currentMedia.lessThan('sp')`
    flex-direction: column;
    align-items: center;
  `}
  ${currentMedia.greaterThan('pc')`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  `}

  width: 100%;
  margin: 15px 0;
  row-gap: 20px;
`

const NoMarginStyledCard = styled(StyledCard)`
  margin-top: 0;
`

const CardHeaderArea = styled(FullWidthRowFlexContainer)<{ status: VOTING_PREFERENCE_STATUS_SUMMARY }>`
  padding: 10px 15px;
  background: ${props => props.status === VOTING_PREFERENCE_STATUS_SUMMARY.BEFORE_NET_RESPONDED
      ? `${Colors.LightBlue};` : `${Colors.Background};`
  }
`

const DeadlineArea = styled.div`
  font-size: ${FontSize.Middle};
`

const VotingPreferenceStatusLabel = styled.div<{ status: VOTING_PREFERENCE_STATUS_SUMMARY }>`
  text-align: center;
  font-size: ${FontSize.Small};
  height: 18px;
  width: fit-content;
  min-width: 70px;
  padding: 0 10px;
  box-sizing: border-box;
  line-height: normal;
  white-space: nowrap;
  user-select: none;

  ${props => {
    switch (props.status) {
      case VOTING_PREFERENCE_STATUS_SUMMARY.BEFORE_NET_RESPONDED:
        return `color: ${Colors.Body};
                background: ${Colors.LightBlue};`
      case VOTING_PREFERENCE_STATUS_SUMMARY.RESPONDED:
        return `color: #FFF;
                background: linear-gradient(to bottom right, #009688BF 50%, #00000000 50%), #00838A;`
      case VOTING_PREFERENCE_STATUS_SUMMARY.CLOSED:
        return `color: #FFF;
                background: ${Colors.AccentDark};`
    }
  }}
`

const FullHeightColumnFlexContainer = styled(ColumnFlexContainer)`
  height: 100%;
`

const CardMainArea = styled(FullWidthLeftColumnFlexContainer)`
  height: 100%;
  padding: 10px 15px;
  gap: 5px;
  justify-content: space-between;
`

const NameArea = styled.div`
  font-size: ${FontSize.Body};
  color: ${Colors.AccentDark};
  font-weight: ${FontWeights.Bold};
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const StyledCalendarMonth = styled(CalendarMonth)`
  font-size: 16px;
  color: ${Colors.AccentLight};
`

const DateArea = styled(FullWidthLeftRowFlexContainer)`
  height: 21px;
  font-size: ${FontSize.Small};
`

const PastGeneralMeetingButton = styled(StyledSecondaryButton)`
  margin-top: 30px;
`

const ContactType = styled.div`
  margin-top: 5px;
  font-size: ${FontSize.Middle};
`
